import React, { useState } from 'react';
import { ChowBadgeSmall } from '../assets/images';
import api from '../services/api';
import '../styles/InfoWindow.css';
import { CareHome } from '../types';
import {
    formatCost,
    formatFreshness,
    formatInspectionTime,
    formatScore,
    getSingleDayEvents,
    getScoreColors
} from '../utils/careHomeCardFormatting';
import EventPopup from './EventPopup';

interface InfoWindowProps {
    careHome: CareHome;
    userId?: string;
    startingPostCode?: string;
    searchPosition?: number;
    eventFilterActive?: boolean;
}

const InfoWindow: React.FC<InfoWindowProps> = ({ careHome, userId = '', startingPostCode = '', searchPosition = 0, eventFilterActive = false }) => {
    const score = formatScore(careHome.open_score);
    const scoreColors = getScoreColors(careHome.open_score);
    const freshnessText = formatFreshness(careHome.freshness_days);
    const feeDisplay = formatCost(careHome.relative_fees);
    const inspectionTime = formatInspectionTime(careHome.Days_Since_Last_Inspected_CQC);
    const singleDayEvents = getSingleDayEvents(careHome.events);
    const scoreSize = 40;
    const scoreRightOffset = 10;
    const [showEventPopup, setShowEventPopup] = useState(false);

    const trackAction = (action: 'details' | 'contact' | 'manage') => {
        api.trackUserAction({
            regulator_id: careHome.reg_id,
            user_action: action,
            user_id: userId,
            starting_post_code: startingPostCode,
            search_position: searchPosition
        });
    };

    const cardStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%',
        maxWidth: '300px',
        minWidth: '270px',
        margin: 0,
        borderRadius: '12px',
        background: 'transparent',
        color: '#4f5960',
        fontFamily: '"Trebuchet MS", sans-serif',
        overflow: 'visible'
    };

    const headerStyle: React.CSSProperties = {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        minHeight: '38px',
        padding: `0.44rem ${scoreSize + 16}px 0.44rem 0.62rem`,
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        background: 'linear-gradient(135deg, #025150 0%, #0a7f72 100%)',
        color: '#ffffff',
        overflow: 'visible'
    };

    const scoreStyle: React.CSSProperties = {
        position: 'absolute',
        top: '100%',
        right: `${scoreRightOffset}px`,
        transform: 'translateY(-50%)',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${scoreSize}px`,
        height: `${scoreSize}px`,
        borderRadius: '999px',
        border: '2px solid #d9f4dc',
        background: `linear-gradient(160deg, ${scoreColors.color1} 0%, ${scoreColors.color2} 100%)`,
        boxShadow: '0 3px 8px rgba(12, 95, 87, 0.12)',
        color: '#ffffff',
        fontSize: '0.88rem',
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: '-0.02em',
        textAlign: 'center',
        whiteSpace: 'nowrap'
    };

    const bodyStyle: React.CSSProperties = {
        padding: '0.5rem 0.68rem 0.5rem',
        border: '1px solid rgba(2, 81, 80, 0.12)',
        borderTop: 0,
        borderBottomLeftRadius: '12px',
        borderBottomRightRadius: '12px',
        background: '#f4f3f1'
    };

    const inspectionMetaStyle: React.CSSProperties = {
        display: 'block',
        marginBottom: '0.34rem',
        paddingRight: `${scoreSize + scoreRightOffset - 4}px`,
        color: '#0d5f57',
        fontSize: '0.62rem',
        lineHeight: 1.2,
        paddingBottom: '0.04rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };

    const infoStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '0.6rem'
    };

    const labelStyle: React.CSSProperties = {
        margin: '0 0 0.1rem',
        color: '#727982',
        fontSize: '0.62rem',
        lineHeight: 1.08,
        whiteSpace: 'nowrap'
    };

    const valueStyle: React.CSSProperties = {
        margin: 0,
        color: '#0d5f57',
        fontSize: '0.78rem',
        lineHeight: 1.02,
        fontWeight: 700
    };

    const dividerStyle: React.CSSProperties = {
        height: '2px',
        margin: '0.3rem 0 0.28rem',
        background: '#1ba395'
    };

    const footerStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '0.32rem',
        flexWrap: 'nowrap'
    };

    const pillStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '26px',
        padding: '0.24rem 0.56rem',
        borderRadius: '14px',
        border: '1.5px solid #d5d8dc',
        background: '#f8f8f8',
        color: '#4f5960',
        textDecoration: 'none',
        fontSize: '0.68rem',
        lineHeight: 1
    };

    const manageLinkStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: '0.24rem',
        marginTop: '0.32rem',
        textDecoration: 'none',
        fontSize: '0.62rem',
        color: '#4f5960',
        opacity: 0.75,
        transition: 'opacity 0.2s ease'
    };

    return (
        <div className="info-window-card" style={cardStyle}>
            <header style={headerStyle}>
                {eventFilterActive ? (
                    <div className="info-window-card__score" style={{
                        ...scoreStyle,
                        background: 'none',
                        border: 'none',
                        boxShadow: 'none',
                        padding: 0
                    }}>
                        <img src={ChowBadgeSmall} alt="Care Home Open Week" width={scoreSize} height={scoreSize} style={{ borderRadius: '999px', display: 'block' }} />
                    </div>
                ) : (
                    <div className="info-window-card__score" style={scoreStyle}>
                        {score}
                    </div>
                )}
            </header>

            <div style={bodyStyle}>
                <div style={inspectionMetaStyle}>
                    <span>Regulator last inspection: {inspectionTime}</span>
                </div>

                <div style={infoStyle}>
                    <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <p style={labelStyle}>Data Last Validated:</p>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                            <p style={valueStyle}>{freshnessText}</p>
                            {careHome.is_premium && (
                                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: '12px', height: '12px', flex: '0 0 auto', display: 'block' }}>
                                    <circle cx="12" cy="12" r="10" fill="#0a7f72" />
                                    <path d="M8 12.2l2.4 2.5L16.5 9" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </div>
                    </div>
                    {/* Hide fees/pound signs when an event filter is active (update 2026_04_22.3) */}
                    {!eventFilterActive && (
                        <div style={{ flex: '0 0 76px', minWidth: '76px' }}>
                            <p style={labelStyle}>Fee</p>
                            <p style={{ ...valueStyle, letterSpacing: '0.04em' }}>
                                <span>{feeDisplay.active}</span>
                                {feeDisplay.muted && (
                                    <span style={{ color: '#c8ccd1' }}>{feeDisplay.muted}</span>
                                )}
                            </p>
                        </div>
                    )}
                </div>

                <div style={dividerStyle} />

                <div style={footerStyle}>
                    {singleDayEvents.length > 0 && (
                        <button
                            className="info-window-card__event-btn"
                            style={{
                                display: 'inline-flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                minHeight: '26px',
                                padding: '0.2rem 0.5rem',
                                borderRadius: '14px',
                                border: '1.5px solid #7b43a8',
                                background: 'linear-gradient(135deg, #7a43a4 0%, #8a56b0 100%)',
                                boxShadow: '0 4px 10px rgba(106, 27, 154, 0.16)',
                                color: '#ffffff',
                                fontSize: '0.64rem',
                                fontWeight: 700,
                                lineHeight: 1.15,
                                cursor: 'pointer',
                                marginRight: 'auto',
                                whiteSpace: 'nowrap',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowEventPopup(true);
                            }}
                        >
                            <span>Event: {singleDayEvents[0].formattedDate}</span>
                            {singleDayEvents.length > 1 && (
                                <span style={{ fontSize: '0.54rem', fontWeight: 500, color: '#ead8f6', marginTop: '1px' }}>
                                    + {singleDayEvents.length - 1} more&hellip;
                                </span>
                            )}
                        </button>
                    )}
                    <a
                        href={`https://www.autumna.co.uk/providers/${careHome.slug}/?utm_source=OpenScoreMap&utm_medium=details`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="info-window-card__action"
                        style={pillStyle}
                        onClick={(e) => {
                            e.stopPropagation();
                            trackAction('details');
                        }}
                    >
                        Details
                    </a>
                    <a
                        href={`https://www.autumna.co.uk/providers/contact/${careHome.id}/?utm_source=OpenScoreMap&utm_medium=contact`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="info-window-card__action info-window-card__action--contact"
                        style={pillStyle}
                        onClick={(e) => {
                            e.stopPropagation();
                            trackAction('contact');
                        }}
                    >
                        Contact
                    </a>
                </div>

                <a
                    href="https://www.openscore.org.uk/care-homes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="info-window-card__manage"
                    style={manageLinkStyle}
                    onClick={(e) => {
                        e.stopPropagation();
                        trackAction('manage');
                    }}
                >
                    <span>Responsible for managing this home?</span>
                    <svg width="9" height="9" viewBox="0 0 18 18" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.58333 17.25C2.07917 17.25 1.64757 17.0705 1.28854 16.7115C0.929514 16.3524 0.75 15.9208 0.75 15.4167V2.58333C0.75 2.07917 0.929514 1.64757 1.28854 1.28854C1.64757 0.929514 2.07917 0.75 2.58333 0.75H9V2.58333H2.58333V15.4167H15.4167V9H17.25V15.4167C17.25 15.9208 17.0705 16.3524 16.7115 16.7115C16.3524 17.0705 15.9208 17.25 15.4167 17.25H2.58333ZM6.89167 12.3917L5.60833 11.1083L14.1333 2.58333H10.8333V0.75H17.25V7.16667H15.4167V3.86667L6.89167 12.3917Z" />
                    </svg>
                </a>
            </div>

            {showEventPopup && (
                <EventPopup
                    events={singleDayEvents}
                    careHomeName={careHome.organisation_name}
                    regulatorId={careHome.reg_id}
                    userId={userId}
                    startingPostCode={startingPostCode}
                    searchPosition={searchPosition}
                    onClose={() => setShowEventPopup(false)}
                />
            )}
        </div>
    );
};

export default InfoWindow;
